<!-- this is a wrapper around an element that needs file upload dropzone support -->
<template>
    <div ref="container" class="relative">
        <template v-if="isUploadEnabled">
            <Transition name="fade">
                <div
                    v-if="canUploadDocuments"
                    class="absolute inset-0 flex items-center justify-center gap-4 text-control-accent border-4 border-dashed border-control-accent transition-colors rounded-lgr"
                    :class="[isOverContainerDropzone ? 'bg-dropzone-bg-active' : 'bg-dropzone-bg']">
                    <i class="bi bi-image text-2xl/none" aria-hidden="true" />
                    <p class="body-semibold">Drop files here to add to chat</p>
                </div>
            </Transition>
            <slot :click-input="handleClickInput"></slot>
            <input
                ref="fileInput"
                type="file"
                aria-hidden
                :accept="acceptedFileTypes ?? fileTypes"
                :multiple="isMultipleFileUploadEnabled"
                class="hidden"
                @change="handleFileChange" />
        </template>
        <div v-else>
            <BaseNotification theme="error" text="File upload is not enabled." hide-dismiss />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDropZone } from "@vueuse/core";
import { useFileUpload } from "~vue/composables/useFileUpload";
import { computed, useTemplateRef } from "vue";

import BaseNotification from "./BaseNotification.vue";

interface Props {
    isDocumentDropzoneEnabled?: boolean;
    isDisabled?: boolean;
    acceptedFileTypes?: string;
    isMultipleFileUploadEnabled?: boolean;
}

const { isDocumentDropzoneEnabled = false, isDisabled = false, acceptedFileTypes, isMultipleFileUploadEnabled = false } = defineProps<Props>();

const emit = defineEmits<{
    fileUpload: [files: File[]];
}>();

const { acceptedFileTypes: fileTypes, isUploadEnabled } = useFileUpload();

const canUploadDocuments = computed(() => {
    if (isDisabled) {
        return false;
    }
    if (isDocumentDropzoneEnabled) {
        return isOverDocumentBody.value;
    }

    return isOverContainerDropzone.value;
});

const dropZoneContainer = useTemplateRef<HTMLDivElement>("container");
// this is the actual drop area where files are processed on drop
const { isOverDropZone: isOverContainerDropzone } = useDropZone(dropZoneContainer, {
    onDrop: (files: File[] | null) => {
        if (files) {
            emit("fileUpload", files);
        }
    },
    multiple: isMultipleFileUploadEnabled,
    preventDefaultForUnhandled: true,
});

// this second dropzone triggers on the body element so that the drag an drop area on chat message control can be seen
const { isOverDropZone: isOverDocumentBody } = useDropZone(document.body, {
    preventDefaultForUnhandled: true,
});

const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const handleClickInput = () => {
    if (isDisabled) {
        return;
    }
    fileInput.value?.click();
};

const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
        emit("fileUpload", Array.from(files));
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
